import React from 'react';

/**
 * UI mockups (HTML/CSS only) for WSJ Pedidos landing hero — no screenshots.
 */
export function WsjPedidosHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[380px] sm:min-h-[440px] lg:min-h-[480px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-amber-500/[0.07] via-transparent to-emerald-500/[0.06] blur-2xl pointer-events-none"
        aria-hidden
      />

      {/* Order card */}
      <div
        className="absolute left-0 top-4 sm:top-8 w-[min(100%,260px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(0,0,0,0.85)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500"
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">{mock.orderLabel}</span>
          <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
            {mock.orderBadge}
          </span>
        </div>
        <p className="text-sm font-medium text-white mb-1">{mock.orderCustomer}</p>
        <div className="flex items-end justify-between mt-4 pt-3 border-t border-white/[0.08]">
          <div className="space-y-1.5">
            <div className="h-2 w-24 rounded-full bg-white/10" />
            <div className="h-2 w-16 rounded-full bg-white/[0.06]" />
          </div>
          <span className="text-lg font-black text-amber-400 tabular-nums">{mock.orderTotal}</span>
        </div>
      </div>

      {/* Agenda card */}
      <div
        className="absolute right-0 top-0 w-[min(100%,240px)] rounded-2xl border border-white/[0.12] bg-slate-950/75 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.75)] p-3.5 z-10 rotate-[3deg] sm:right-4 lg:right-8"
      >
        <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2.5">{mock.agendaLabel}</p>
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { t: '09:00', s: 'free' },
            { t: '10:00', s: 'busy' },
            { t: '11:00', s: 'free' },
            { t: '14:00', s: 'busy' },
            { t: '15:00', s: 'free' },
            { t: '16:00', s: 'free' }
          ].map((cell) => (
            <div
              key={cell.t}
              className={`rounded-lg px-1.5 py-2 text-center text-[10px] font-medium ${
                cell.s === 'busy'
                  ? 'bg-white/[0.04] text-white/25 line-through border border-white/[0.06]'
                  : 'bg-emerald-500/15 text-emerald-200/90 border border-emerald-500/25'
              }`}
            >
              <div className="tabular-nums opacity-90">{cell.t}</div>
              <div className="text-[8px] mt-0.5 opacity-70">{cell.s === 'busy' ? mock.slotBusy : mock.slotFree}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Product card */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-6 sm:bottom-10 w-[min(100%,280px)] rounded-2xl border border-amber-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/80 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(212,165,116,0.35)] p-4 z-30"
      >
        <div className="flex gap-3">
          <div className="h-14 w-14 shrink-0 rounded-xl bg-gradient-to-br from-amber-500/40 to-amber-600/20 border border-amber-500/30" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-bold text-white truncate">{mock.productLabel}</p>
            <p className="text-[10px] text-white/45 mt-1 leading-snug">{mock.productMeta}</p>
            <p className="text-sm font-black text-amber-400 mt-2 tabular-nums">{mock.productPrice}</p>
          </div>
        </div>
      </div>

      {/* Busy / blocked pill */}
      <div className="absolute right-6 sm:right-12 bottom-28 sm:bottom-36 z-40 max-w-[200px]">
        <div className="rounded-xl border border-red-500/25 bg-red-950/40 backdrop-blur-md px-3 py-2 shadow-lg">
          <p className="text-[11px] font-bold text-red-200/95 leading-tight">{mock.blockTitle}</p>
          <p className="text-[9px] text-red-200/55 mt-1 leading-snug">{mock.blockSubtitle}</p>
        </div>
      </div>
    </div>
  );
}

/** Mini week calendar for agenda section */
export function WsjPedidosAgendaVisual() {
  const days = ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'];
  return (
    <div className="rounded-2xl border border-white/[0.1] bg-slate-950/50 backdrop-blur-md p-4 sm:p-6 max-w-md mx-auto lg:mx-0">
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map((d, i) => (
          <div key={i} className="text-center text-[10px] font-semibold text-white/35 py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {Array.from({ length: 21 }).map((_, i) => {
          const busy = i === 5 || i === 6 || i === 12 || i === 15;
          return (
            <div
              key={i}
              className={`aspect-square rounded-lg text-[9px] flex items-center justify-center font-medium ${
                busy
                  ? 'bg-white/[0.04] text-white/20 border border-white/[0.05]'
                  : 'bg-emerald-500/12 text-emerald-200/80 border border-emerald-500/20'
              }`}
            >
              {busy ? '—' : i + 1}
            </div>
          );
        })}
      </div>
    </div>
  );
}
